import type {
  BlogPost,
  BlogPostWithContent,
  CreatePostRequest,
  UpdatePostRequest,
  PostFilter,
  PostListResponse,
} from '@/types/blog';

const BLOG_API_BASE = '/api/v1/blog';

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response;
}

// =============================================================================
// Protected APIs (admin, require auth)
// =============================================================================

export async function createPost(request: CreatePostRequest): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

export async function getPost(id: string): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}`);
  return response.json();
}

export async function listPosts(filter?: PostFilter): Promise<PostListResponse> {
  const params = new URLSearchParams();
  if (filter?.status) params.append('status', filter.status);
  if (filter?.category) params.append('category', filter.category);
  if (filter?.limit) params.append('limit', filter.limit.toString());
  if (filter?.offset) params.append('offset', filter.offset.toString());

  const url = params.toString() ? `${BLOG_API_BASE}/posts?${params}` : `${BLOG_API_BASE}/posts`;
  const response = await fetchWithAuth(url);
  return response.json();
}

export async function updatePost(id: string, request: UpdatePostRequest): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  return response.json();
}

export async function deletePost(id: string): Promise<void> {
  await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}`, { method: 'DELETE' });
}

export async function publishPost(id: string): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}/publish`, { method: 'POST' });
  return response.json();
}

export async function unpublishPost(id: string): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}/unpublish`, { method: 'POST' });
  return response.json();
}

export async function archivePost(id: string): Promise<BlogPost> {
  const response = await fetchWithAuth(`${BLOG_API_BASE}/posts/${id}/archive`, { method: 'POST' });
  return response.json();
}

// =============================================================================
// Public APIs (no auth required - for landing page)
// =============================================================================

export async function listPublishedPosts(limit?: number): Promise<BlogPost[]> {
  const url = limit
    ? `${BLOG_API_BASE}/public/posts?limit=${limit}`
    : `${BLOG_API_BASE}/public/posts`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch posts');
  return response.json();
}

export async function getPostBySlug(slug: string): Promise<BlogPostWithContent> {
  const response = await fetch(`${BLOG_API_BASE}/public/posts/${slug}`);
  if (!response.ok) throw new Error('Post not found');
  return response.json();
}

// =============================================================================
// Helper functions
// =============================================================================

export function getDocEditUrl(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/edit`;
}

export function getDocViewUrl(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/view`;
}
